import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { Path, Line, Group, Circle } from 'react-konva';
import { radToDeg, colorToArray, isColorBright, RGB_2_HEX } from '../../lib/ep';

const GameShooter = forwardRef(({ shooter_props, ...props }, ref) => {
	const real_shooter_props = { ...shooter_props };
	if(real_shooter_props.scaleX != undefined) delete real_shooter_props.scaleX;
	if(real_shooter_props.scaleY != undefined) delete real_shooter_props.scaleY;

	const size = 150 * (shooter_props?.scaleX ?? 1);
	const max_opacity = 0.3;

	const main_stage_axis = props?.main_stage_axis ?? 600;

	const is_debug = props?.is_debug ?? false;
	const custom_trajectory = props?.custom_trajectory ?? false;

	const max_shot_line_length = 1000;

	let shot_line_length;
	switch(props?.game_display_path ?? 'full') {
		case 'short':
			shot_line_length = main_stage_axis / 5;
			break;

		case 'none':
			shot_line_length = 0;
			break;

		case 'normal':
			shot_line_length = main_stage_axis / 3;
			break;

		case 'full':
		default:
			shot_line_length = max_shot_line_length;
			break;
	}
	shot_line_length = Math.max(10, shot_line_length);

	const hide_shot_line = (shot_line_length < 11) ? true : false;

	const color_raw = props?.color ?? 'rgba(255, 255, 255, 1)';
	const color_arr = colorToArray(color_raw, false);
	const color = color_arr.join(", ");
	const stroke_color = (isColorBright(RGB_2_HEX(color_raw))) ? 'black' : 'white';

	const apply_heisenberg = props?.apply_heisenberg ?? 'normal';

	const force = useRef(0);
	const angle = useRef(0);
	const refs = useRef([]);
	const enabled = useRef(true);

	const reset_default_props = {
		duration:0.5,
		easing:Konva.Easings.BounceEaseOut,
	};

	useImperativeHandle(ref, () => ({
		moveTo(coords) {
			refs.current['whole'].setAttrs(coords);
		},

		updateTrajectory(points, extra_props = {}) {
			if(custom_trajectory === false) return;

			if(refs.current['shot_line'] == undefined) return;

			refs.current['shot_line'].setAttrs({
				...extra_props,
				opacity:1,
				points,
			});
		},

		fadeTrajectory(duration = 1) {
			if(refs.current['shot_line'] == undefined) return;

			refs.current['shot_line'].to({
				opacity:0,
				duration,
				easing:Konva.Easings.StrongEaseInOut,
			});
		},

		disableShooter() {
			if(refs.current['shooter'] == undefined) return;

			refs.current['shooter'].setAttrs({
				x:-1000,
				draggable:false,
			});
		},

		enableShooter() {
			if(refs.current['shooter'] == undefined) return;

			refs.current['shooter'].setAttrs({
				x:0,
				draggable:true,
			});
		},

		showTrajectory() {
			if(refs.current['shot_line'] == undefined) return;

			refs.current['shot_line'].setAttrs({
				opacity:1,
			});
		},

		hideTrajectory() {
			if(refs.current['shot_line'] == undefined) return;

			refs.current['shot_line'].setAttrs({
				opacity:0,
			});
		},

		resetShooter(anim_props = reset_default_props, cb) {
			angle.current = 0;
			force.current = 0;

			if(refs.current['target'] == undefined) return;

			const default_anim_props = {
				...reset_default_props,
				...anim_props,
			};

			refs.current['target'].to({
				opacity:0,
				x:0,
				y:0,
				...default_anim_props,
			});

			refs.current['shooter'].to({
				opacity:0,
				x:0,
				y:0,
				...default_anim_props,
			});

			refs.current['shooter_area'].to({
				opacity:0,
				...default_anim_props,
			});

			refs.current['shot_line'].to({
				opacity:0,
				...default_anim_props,
			});
	
			refs.current['shooter_line'].to({
				points:[0, 0, 0, 0],
				...default_anim_props,
				onFinish:() => {
					enabled.current = true;
					if(cb != undefined) cb();
				},
			});
		},
	}), []);

	// returns in rad
	const calcAngle = (coords) => {
		// ArcTan for the angle between x (far side) and y (adjacent side):
		const a = Math.atan(coords.x / coords.y);
		if(isNaN(a)) return 0;
		return a;
	};

	const calcForce = (coords) => {
		// hypothenuse between the two sides of the rect-triangle (x and y):
		return Math.sqrt(Math.pow(coords.x, 2) + Math.pow(coords.y, 2));
	};

	const getHeisenbergValue = () => {
		if(shot_line_length < 11) return 0;

		const percent = force.current / size;

		switch(apply_heisenberg) {
			case 'no':
				return 0;

			case 'weak':
				return percent * 50;

			case 'normal':
				return percent * 110;

			case 'intense':
				return percent * 300;
		}
	};

	const getShotLinePoints = () => {
		if(custom_trajectory !== false) {
			return [
				0, 0,
				0, 1,
			];
		}

		const heisenberg = getHeisenbergValue();
		const line_thickness = 3;

		return [
			-line_thickness / 2, 0,
			-(line_thickness + heisenberg) / 2, -max_shot_line_length,
			(line_thickness + heisenberg) / 2, -max_shot_line_length,
			line_thickness / 2, 0,
		];
	};

	const onDragMove = () => {
		if(enabled.current === false) return;

		const shooter = refs.current['shooter'];

		let final_pos = {
			x:Math.min(size, Math.max(-size, shooter.attrs.x)),
			y:Math.max(0, shooter.attrs.y),
		};

		shooter.setAttrs(final_pos);

		angle.current = calcAngle(final_pos);
		force.current = calcForce(final_pos);

		if(force.current > size) {
			force.current = size;

			// we apply the restrictions (the shooter can't be pushed further than the shooter_area):
			final_pos.x = Math.sin(angle.current) * force.current;
			final_pos.y = Math.cos(angle.current) * force.current;
		}

		refs.current['shooter_area'].setAttrs({
			opacity:max_opacity * force.current / size,
		});

		refs.current['shooter_line'].setAttrs({
			points:[0, 0, final_pos.x, final_pos.y],
		});

		if(custom_trajectory === false) {
			refs.current['shot_line'].setAttrs({
				rotation:radToDeg(-angle.current),
				points:getShotLinePoints(),
			});
		}

		refs.current['target'].setAttrs({
			...final_pos,
		});

		if(props.onMove != undefined) props.onMove(final_pos);
		if(props.onDragMove != undefined) props.onDragMove(force.current / size, getAngleWithHeisenberg());
	};

	const getAngleWithHeisenberg = () => {
		const a = radToDeg(angle.current);
		const heisenberg = getHeisenbergValue();
		const max_deviation_angle = radToDeg(Math.asin(heisenberg / max_shot_line_length));
		return a + Math.random() * max_deviation_angle - max_deviation_angle / 2;
	}

	const throwPointer = () => {
		if(enabled.current === false) return;
		enabled.current = false;
		props.onRelease(force.current / size, getAngleWithHeisenberg());
	};

	const onDragStart = () => {
		if(props.onStart != undefined) props.onStart({ x:0, y:0 });

		if(refs.current['target'] == undefined) return;

		refs.current['target'].to({
			opacity:1,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['shot_line'].to({
			opacity:(hide_shot_line) ? 0 : 0.5,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const onDragEnd = () => {
		throwPointer();
	};

	const getAreaPath = () => {
		const foo = 1679 / 3750;
		const p1 = Math.round(100 * size * (1 - foo)) / 100;
		const p2 = Math.round(100 * size * foo) / 100;
		return `M${size},0c0,${p1}-${p2},${size}-${size},${size}S-${size},${p1}-${size},0H${size}z`;
	};

	let trajectory_props = {
		points:getShotLinePoints(),
		opacity:0,
	};

	if(custom_trajectory !== false) {
		trajectory_props = {
			...trajectory_props,
			stroke:`rgba(${color}, 0.5)`,
			strokeWidth:2,
			bezier:true,
			tension:0.5,
		};
	} else {
		trajectory_props = {
			...trajectory_props,
			fill:`rgba(${color}, 0.5)`,
			closed:true,
		};
	}

	return (
		<Group
			ref={(node) => { refs.current['whole'] = node; }}
			{...real_shooter_props}
		>
			<Path
				ref={(node) => { refs.current['shooter_area'] = node; }}
				data={getAreaPath()}
				fill={`rgba(${color}, 1)`}
				opacity={0}
			/>

			<Line
				ref={(node) => { refs.current['shooter_line'] = node; }}
				points={[0, 0, 0, 0]}
				strokeWidth={1}
				stroke={`rgba(${color}, 1)`}
			/>

			<Group
				clipFunc={(ctx) => {
					ctx.arc(0, 0, shot_line_length, 0, Math.PI * 2, false);
				}}
			>
				<Line
					ref={(node) => { refs.current['shot_line'] = node; }}
					{...trajectory_props}
				/>
			</Group>

			<Circle
				ref={(node) => { refs.current['target'] = node; }}
				radius={15}
				fill={`rgba(${color}, 1)`}
				stroke={stroke_color}
				strokeWidth={2}
				opacity={(is_debug) ? 0.2 : 0}
			/>

			<Group
				ref={(node) => { refs.current['shooter'] = node; }}
				draggable={true}
				onDragStart={onDragStart}
				onDragMove={onDragMove}
				onDragEnd={onDragEnd}
			>
				<Circle
					radius={40}
					fill={`rgba(${color}, 1)`}
					opacity={(is_debug) ? 0.2 : 0}
				/>
			</Group>
		</Group>
	)
});

export default GameShooter;